/**
 * ErrorBoundary — catches uncaught errors in the React tree.
 *
 * Deploy at two levels:
 *  1. Root level (main.tsx) — last resort, shows "something went wrong"
 *  2. Page level (each route) — one crashing page doesn't take down the app
 *
 * All caught errors are logged via the logger service and reported via the
 * events service (events.captureException). To capture errors in PostHog or
 * another analytics tool, add a `captureException` handler to the adapter
 * in src/services/events.ts — no other code changes required.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:ERROR-HANDLING
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { logger } from '@/services/logger'
import { events } from '@/services/events'

interface Props {
  children: ReactNode
  /** Custom fallback UI. Receives the error and a reset function. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const context = { componentStack: info.componentStack ?? 'unknown' }
    logger.error('ErrorBoundary caught an error', error, context)
    events.captureException(error, context)
  }

  reset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset)
      }

      return (
        <div
          role="alert"
          className="flex min-h-[200px] flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. Our team has been notified.
          </p>
          <button
            onClick={this.reset}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
